import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { Button } from "@/components/ui/button";
import { Loader2, LogOut } from "lucide-react";

export function EmailVerification() {
  const { currentUser, sendVerificationEmail, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) {
      navigate("/login");
      return;
    }

    if (currentUser.emailVerified) {
      navigate("/");
      return;
    }

    // Check email verification status every 5 seconds
    const interval = setInterval(() => {
      currentUser.reload().then(() => {
        if (currentUser.emailVerified) {
          navigate("/");
        }
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [currentUser, navigate]);

  const handleResendEmail = async () => {
    try {
      setError("");
      setLoading(true);
      await sendVerificationEmail();
      setCountdown(60);
    } catch (err) {
      setError("Failed to resend verification email");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (err) {
      setError("Failed to sign out");
      console.error(err);
    }
  };

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  return (
    <AuthLayout>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">
            Verify your email
          </h2>
          <p className="text-sm text-gray-500">
            We've sent a verification email to {currentUser?.email}. Please
            check your inbox and click the verification link.
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4 text-sm text-red-500">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <Button
            onClick={handleResendEmail}
            disabled={loading || countdown > 0}
            className="w-full"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : countdown > 0 ? (
              `Resend in ${countdown}s`
            ) : (
              "Resend verification email"
            )}
          </Button>

          <Button onClick={handleSignOut} variant="outline" className="w-full">
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </Button>
        </div>
      </div>
    </AuthLayout>
  );
}
